/* File: src\components\game\MatchButton.js */
import React from 'react';

/**
 * MatchButton Component - Displays a button for position or audio match responses.
 * Provides visual feedback based on correctness, missed matches, or acknowledgment.
 * Styles now match the main "Start Training" button from the dashboard.
 *
 * Props:
 * - label: {string} Text displayed on the button (e.g., "Position Match").
 * - keyHint: {string} The keyboard key associated with this button (for accessibility/tooltip).
 * - onClick: {function} Callback executed when the button is clicked.
 * - feedbackState: {string|null} 'correct', 'incorrect', 'missed', 'acknowledged', or null.
 * - disabled: {boolean} Whether the button is interactive.
 */
const MatchButton = ({ label, keyHint, onClick, feedbackState, disabled }) => {
  // console.log(`[MatchButton '${label}'] Received feedbackState: ${feedbackState}, Disabled: ${disabled}`); // Keep logs commented unless debugging

  // Base classes including transition for smooth feedback changes
  let baseClasses = `
    w-full text-white py-2.5 px-4 rounded-lg font-medium text-sm sm:text-base
    transition duration-150 ease-in-out shadow-sm hover:shadow focus:outline-none
    focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500
    flex items-center justify-center
  `; // Transition duration controls the speed of the color change animation

  // Determine background and text color based on state
  let stateClasses = '';
  if (disabled) {
    // Disabled state - Keep distinct (uses indigo-400 but also opacity/cursor)
    stateClasses = 'bg-indigo-400 opacity-60 cursor-not-allowed';
  } else if (feedbackState === 'correct') {
    // Correct feedback (Green)
    stateClasses = 'bg-green-500 hover:bg-green-600';
  } else if (feedbackState === 'incorrect' || feedbackState === 'missed') {
    // Incorrect or Missed feedback (Red)
    stateClasses = 'bg-red-500 hover:bg-red-600';
  } else if (feedbackState === 'acknowledged') {
    // Acknowledged feedback (when instant feedback is off)
    // Use a noticeably lighter Indigo shade for the brief flash
    stateClasses = 'bg-indigo-400 hover:bg-indigo-500';
  } else {
    // Default active state (no feedback active)
    stateClasses = 'bg-indigo-600 hover:bg-indigo-700';
  }

  const finalClasses = `${baseClasses} ${stateClasses}`;
  // console.log(`[MatchButton '${label}'] Applying classes: ${finalClasses}`); // Keep logs commented unless debugging

  return (
    <button
      onClick={!disabled ? onClick : undefined} // Only attach onClick if not disabled
      disabled={disabled}
      className={finalClasses} // Apply the combined base and state-specific classes
      aria-label={`${label} (${keyHint})`} // Accessibility label
      title={`${label} (Key: ${keyHint})`} // Tooltip on hover
    >
      {/* Only display the label text inside the button */}
      <span>{label}</span>
    </button>
  );
};

export default MatchButton;